/*
 * Converters between the data formats required by the DataGrid, and other
 * application data formats
 */

import { ComponentType, createElement, Dispatch, SetStateAction } from "react";
import {
  Column as RdgColumn,
  FormatterProps as RdgFormatterProps,
  HeaderRendererProps,
  SortColumn as RdgSortColumn,
} from "react-data-grid";

import { FilterCriteria, SupportedDataTypeNames, SupportedDataTypes } from "../../../../models";
import { logError } from "../../../../service/error";
import { ResultData } from "../../../../service/Shared";
import { toNumber } from "../clientSide/ClientSideFilter";
import { cellFormatters } from "../components/CellFormatters";
import { HeaderRenderer, onFilterChangedEvent } from "../components/HeaderRenderer";
import { ColumnDefinition, ColumnDefinitionWithCustomCellFormatter } from "../models";

// maps `FilterCriteria` in flattened DataGrid format to a nested value
// e.g. `{ "a.b.c": { operator: "eq", value: "John" } }`
// becomes `{ a: { b: { c: { operator: "eq", value: "John" } } } }`
// NCU: this function is dynamic by nature -> `any` is required
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mapFilterCriteria = (filterCriteria: FilterCriteria[]): any => {
  // NCU: this function is dynamic by nature -> `any` is required
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const all: any = {};
  for (let i = 0; i < filterCriteria.length; i++) {
    // NCU: this function is dynamic by nature -> `any` is required
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let current: any = all;
    filterCriteria[i].key.split(".").forEach((prop) => {
      current[prop] = current[prop] || {};
      current = current[prop];
    });
    current.operator = filterCriteria[i].operator;
    current.value = filterCriteria[i].value;
  }
  return all;
};

// How I want to calculate minWidth:
// 1. If minWidth is provided in the column definition, use that
// 2. Else minWidth is based on the longest cell value for the column (falling back to the column name if there is no data) + some padding

const getCellText = (cellValue: ResultData[string]): string => {
  if (cellValue === null || cellValue === undefined) return "";
  if (cellValue instanceof Date) return cellValue.toISOString();
  if (typeof cellValue === "object" && "text" in cellValue) {
    const { text } = cellValue as { text?: string };
    if (typeof text === "string") return text;
  }
  return String(cellValue);
};

export const calculateMinWidth = (
  columnDefinition: ColumnDefinition,
  dataGridSortable: boolean,
  rows?: readonly ResultData[]
): number | undefined => {
  if (columnDefinition.minWidth !== undefined) {
    return columnDefinition.minWidth;
  }
  const name = columnDefinition.name ?? columnDefinition.key[0].toUpperCase() + columnDefinition.key.substring(1);
  // Skip raw-value measurement for custom formatters — they can render anything (e.g. a URL as an icon
  // button), so the raw string length has no relation to the actual rendered width.
  const longestCellLength =
    columnDefinition.formatter === "custom"
      ? 0
      : (rows?.reduce((longest, row) => Math.max(longest, getCellText(row[columnDefinition.key]).length), 0) ?? 0);
  const baseLength = Math.max(longestCellLength, name.length);
  const minWidth = baseLength * 7.5 + 50; // 7.05 is an approximate average width of a character in pixels for the default font

  return minWidth;
};

export const mapColumnDefinitionToRdgColumn = (
  columns: ColumnDefinition[],
  dataGridSortable: boolean,
  dataGridFilterable: boolean,
  sortColumns: readonly RdgSortColumn[],
  setSortColumns: Dispatch<SetStateAction<readonly RdgSortColumn[]>>,
  filtersEnabled: boolean,
  filterCriteria: FilterCriteria[],
  setFilterCriteria: Dispatch<SetStateAction<FilterCriteria[]>>,
  onFilterChanged: onFilterChangedEvent,
  rows?: readonly ResultData[]
): RdgColumn<ResultData>[] => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return columns.map((columnDefinition) => {
    let formatter: ComponentType<RdgFormatterProps<ResultData>> | undefined;
    if (columnDefinition.formatter !== undefined) {
      let firstLevelFormatterFn = cellFormatters[columnDefinition.formatter];
      if (firstLevelFormatterFn === undefined && columnDefinition.formatter === "custom") {
        firstLevelFormatterFn = (columnDefinition as ColumnDefinitionWithCustomCellFormatter<SupportedDataTypes>)
          .customCellFormatter;
      }
      if (firstLevelFormatterFn === undefined)
        throw new Error(
          `Column definition formatter is ${columnDefinition.formatter}; expecting a "customCellFormatter" to be supplied, but was undefined`
        );
      const secondLevelFormatterFn = firstLevelFormatterFn(columnDefinition);
      const { key } = columnDefinition;
      formatter = (props) => secondLevelFormatterFn(props.row[key] as never);
    }

    return {
      key: columnDefinition.key,
      name: columnDefinition.name ?? columnDefinition.key[0].toUpperCase() + columnDefinition.key.substring(1),
      minWidth: calculateMinWidth(columnDefinition, dataGridSortable, rows),
      width: columnDefinition.width,
      formatter,
      sortable: (columnDefinition.sortable === undefined && dataGridSortable) || columnDefinition.sortable,
      // Render HeaderRenderer as a component (not a hook-calling function invoked in this map) so
      // react-data-grid mounts one instance per header cell — each with its own hook scope. This
      // keeps the Rules of Hooks satisfied when the column set changes length between renders.
      headerRenderer: (props: HeaderRendererProps<ResultData, void>) =>
        createElement(HeaderRenderer, {
          ...props,
          onFilterChanged,
          columnDefinition,
          dataGridFilterable,
          dataGridSortable,
          sortColumns,
          setSortColumns,
          filtersEnabled,
          filterCriteria,
          setFilterCriteria,
        }),
    };
  });
};

const castFilterValueToCorrectType = (dataType: SupportedDataTypeNames, filter: FilterCriteria): FilterCriteria => {
  switch (dataType) {
    case "string": {
      const updatedFilter = filter;
      updatedFilter.value = (filter.value as string).toLowerCase();

      return updatedFilter;
    }
    case "number": {
      const updatedFilter = filter;

      if (filter.value === "") {
        // If value is empty (when a filter is first selected or when input data is deleted), default to `eq` filter
        // This makes it so that this filter can be removed from the filterCriteria by using the `filter` function in getFilterCriteriaWithCastedValues
        updatedFilter.operator = "eq";
      }

      // Defaults the filter to 0 if the inputted value cannot be converted to a number
      updatedFilter.value = toNumber(filter.value as number) ?? 0;

      return updatedFilter;
    }
    // TODO treat other types like boolean and date
    default:
      logError({ error: `Couldn't cast the filter value; unexpected dataType: ${dataType}` });
      throw new Error(`Couldn't cast the filter value; unexpected dataType: ${dataType}`);
  }
};

export const getFilterCriteriaWithCastedValues = (
  filterCriteria: FilterCriteria[],
  columns: ColumnDefinition[]
): FilterCriteria[] => {
  const filterColumnDefinitions = new Array<ColumnDefinition>(filterCriteria.length);

  for (let i = 0; i < filterCriteria.length; i++) {
    const filterColumnDefinition = columns.find((cd) => cd.key === filterCriteria[i].key);

    if (!filterColumnDefinition) {
      logError({
        error: `Couldn't find ColumnDefinition for column key ${filterCriteria[i].key} in useCursorDataGrid effect`,
      });
      throw new Error(
        `Couldn't find ColumnDefinition for column key ${filterCriteria[i].key} in useCursorDataGrid effect`
      );
    }

    filterColumnDefinitions[i] = filterColumnDefinition;
  }

  let updatedFilterCriteria = filterCriteria;

  for (let i = 0; i < filterColumnDefinitions.length; i++) {
    // `filterColumnDefinitions` was created from `filterCriteria`, this `find` will always return a value
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const filterToBeCasted = updatedFilterCriteria.find((fc) => fc.key === filterColumnDefinitions[i].key)!;

    updatedFilterCriteria[i] = castFilterValueToCorrectType(filterColumnDefinitions[i].dataType, filterToBeCasted);
  }

  updatedFilterCriteria = updatedFilterCriteria.filter((ufc) => !(ufc.operator === "eq" && Number.isNaN(ufc.value)));

  return updatedFilterCriteria;
};
